// ─── Il fuso, in un punto solo (§ 03.2) ─────────────────────────────────────
//
// 🕰️ Quasi tutto il calendario è già in ora di Roma quando arriva: a
// convertirlo è Postgres (⇒ `apps/api/src/modules/calendario/deposito.ts`), e
// da questa parte si fa solo aritmetica di stringhe e di minuti.
//
// ⇒ Qui resta l'unica cosa che il server non può sapere: **che giorno è
//   adesso**, secondo il calendario del circolo e non secondo quello del
//   computer su cui la pagina è aperta.

const FUSO = 'Europe/Rome';

/**
 * Oggi, per il circolo.
 *
 * 🚨 `sv-SE` non è un vezzo: è l'unico formato locale che rende una data già
 * scritta `AAAA-MM-GG`. L'alternativa — `toISOString().slice(0,10)` — dà il
 * giorno **UTC**, e per due ore ogni sera (una d'inverno) direbbe ieri.
 */
export function oggi(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: FUSO }).format(new Date());
}

/** `2026-09-15` più `n` giorni. Aritmetica di calendario, nessun fuso di mezzo. */
export function piuGiorni(data: string, n: number): string {
  return new Date(Date.parse(`${data}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
}

const GIORNI_LUNGHI = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
const MESI = [
  'gennaio',
  'febbraio',
  'marzo',
  'aprile',
  'maggio',
  'giugno',
  'luglio',
  'agosto',
  'settembre',
  'ottobre',
  'novembre',
  'dicembre',
];

/** `lunedì 14 settembre 2026`. Composto a mano: nessuna sorpresa di locale. */
export function giornoPerEsteso(data: string): string {
  const d = new Date(`${data}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return data;
  return `${GIORNI_LUNGHI[d.getUTCDay()]} ${d.getUTCDate()} ${MESI[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** `lun 14/09`, per i bottoni stretti. */
export function giornoBreve(data: string): string {
  const d = new Date(`${data}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return data;
  return `${GIORNI_LUNGHI[d.getUTCDay()]!.slice(0, 3)} ${String(d.getUTCDate()).padStart(2, '0')}/${String(
    d.getUTCMonth() + 1,
  ).padStart(2, '0')}`;
}

/**
 * 💶 `2026-09` → `Settembre 2026`, per la barra dei mesi.
 *
 * ⚖️ **Torna con la maiuscola, al contrario di tutto il resto di questo file**,
 * e non è un'incoerenza: `giornoPerEsteso` finisce dentro una frase
 * (*«dal lunedì 14 settembre al…»*), questo è un **titolo che sta da solo**
 * fra due frecce. Metterla a valle vorrebbe dire una funzioncina in più nella
 * schermata, cioè lo stesso pezzo di logica in due posti.
 *
 * ⚠️ Chirurgia di stringhe e nessun `Date`: un `new Date('2026-09')` sarebbe un
 * istante, e un istante al cambio dell'ora può cadere nel mese prima.
 */
export function mesePerEsteso(mese: string): string {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(mese)) return mese;
  const nome = MESI[Number(mese.slice(5, 7)) - 1]!;
  return `${nome[0]!.toUpperCase()}${nome.slice(1)} ${mese.slice(0, 4)}`;
}

/**
 * `2026-09` più `n` mesi — `piuGiorni`, ma per la barra dei mesi.
 *
 * ⛔ Non si fa con `piuGiorni(…, 30)`: i mesi non sono lunghi uguali, e un
 * passo di trenta giorni salterebbe febbraio e resterebbe due volte su marzo.
 * ⚖️ Il conto passa dai **mesi assoluti** (`anno * 12 + mese`) proprio per non
 * dover ragionare sui riporti di dicembre.
 */
export function piuMesi(mese: string, n: number): string {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(mese)) return mese;
  const assoluti = Number(mese.slice(0, 4)) * 12 + (Number(mese.slice(5, 7)) - 1) + n;
  const anno = Math.floor(assoluti / 12);
  const m = assoluti - anno * 12 + 1;
  return `${anno}-${String(m).padStart(2, '0')}`;
}

/** `giovedì`, e basta: serve a dire «ogni giovedì» senza ripetere la data. */
export function giornoDellaSettimana(data: string): string {
  const d = new Date(`${data}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? data : GIORNI_LUNGHI[d.getUTCDay()]!;
}

/** `14 settembre`, senza l'anno: dentro una frase l'anno è quasi sempre rumore. */
export function giornoSenzaAnno(data: string): string {
  const d = new Date(`${data}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? data : `${d.getUTCDate()} ${MESI[d.getUTCMonth()]}`;
}

/**
 * ⭐⭐ **LA DATA COME LA SCRIVE UN ITALIANO** — `2027-04-22` → `22/04/2027`.
 *
 * 🗣️ Richiesta sua del 17/09/2026: *«Sì, la data scrivila tu in italiano»*,
 * dopo aver visto una casella «Fino al» che mostrava `04/22/2027`.
 *
 * 🚨 **Perché non bastava una traduzione: quel formato NON era dell'app.** Un
 * `<input type="date">` lo disegna il **browser**, con la lingua del browser —
 * non con quella della pagina, non con `lang`, non con `locale`. ⇒ Non c'è
 * nessuna riga da correggere: c'è una casella da smettere di usare per mostrare.
 * 📌 *Un pezzo di interfaccia che nessuna riga di codice governa non è
 *    configurato male: è fuori dalle nostre mani, e l'unico modo di riprenderlo
 *    è disegnarlo noi.*
 *
 * ⚠️ Chirurgia di stringhe e non un `Date`: la data ISO **è già** il giorno
 * giusto, e farla passare per un istante è il modo classico di perdere un giorno
 * al cambio dell'ora.
 */
export function inItaliano(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

/**
 * 🕰️ Un ISTANTE (`2026-09-22T16:40:00Z`) come lo legge la segreteria: «oggi alle
 * 18:40» se è di oggi, altrimenti «22/09/2026 18:40» — sempre in ora di Roma.
 *
 * ⚖️ Qui un `Date` ci vuole, a differenza di `inItaliano`: il server manda un
 * istante, non un giorno, e il giorno giusto dipende dal fuso. Nato per le
 * chiavi del ponte (voce 20), dove «ultimo uso: oggi alle…» è ciò che dice se
 * il bot la sta usando.
 */
export function istanteInItaliano(iso: string): string {
  const d = new Date(iso);
  const giorno = new Intl.DateTimeFormat('sv-SE', { timeZone: FUSO }).format(d);
  const ora = new Intl.DateTimeFormat('it-IT', { timeZone: FUSO, hour: '2-digit', minute: '2-digit' }).format(
    d,
  );
  if (giorno === oggi()) return `oggi alle ${ora}`;
  return `${inItaliano(giorno)} ${ora}`;
}

/**
 * ⭐⭐ **E COME LA BATTE** — `22/4/27`, `22-04-2027`, `22042027`, `22 4 2027`:
 * tutte quante rendono `2027-04-22`. Ciò che non è una data rende `null`, e chi
 * chiama decide cosa farne.
 *
 * ⚖️ **Perché così larga.** Al banco si scrive in fretta, e un campo che accetta
 * una sola forma costringe a rifare. I separatori non aggiungono ambiguità —
 * l'ordine è sempre giorno, mese, anno — quindi rifiutarli sarebbe severità
 * senza guadagno.
 *
 * 🔒 **`annoCorto` è spento mentre si scrive, acceso quando si esce dal campo**,
 * ed è la differenza fra un aiuto e un dispetto: battendo `22/04/2027`, dopo la
 * terza cifra si passa per `22/04/20` — che come anno corto è il **2020**, e la
 * riga viva sotto direbbe una cosa assurda a metà di ogni battuta.
 *
 * ⛔ E il 31 febbraio si rifiuta invece di scivolare al 3 marzo, che è quello
 * che farebbe `Date` da solo: una data corretta in silenzio è una prenotazione
 * su un giorno che nessuno ha scelto.
 */
export function daItaliano(testo: string, annoCorto = false): string | null {
  const pezzi = testo
    .trim()
    .split(/[^0-9]+/)
    .filter(Boolean);
  let g: string;
  let m: string;
  let a: string;
  if (pezzi.length === 3) [g, m, a] = pezzi as [string, string, string];
  else if (pezzi.length === 1 && (pezzi[0]!.length === 8 || (annoCorto && pezzi[0]!.length === 6))) {
    const c = pezzi[0]!;
    [g, m, a] = [c.slice(0, 2), c.slice(2, 4), c.slice(4)];
  } else return null;

  if (a.length === 2) {
    if (!annoCorto) return null;
    a = `20${a}`;
  }
  if (a.length !== 4 || g.length > 2 || m.length > 2) return null;

  const [anno, mese, giorno] = [Number(a), Number(m), Number(g)];
  const d = new Date(Date.UTC(anno, mese - 1, giorno));
  // ⛔ Il giro di controllo: `Date` accetta il 31 febbraio e lo sposta. Se ciò
  // che esce non è ciò che è entrato, quel giorno non esiste.
  if (d.getUTCFullYear() !== anno || d.getUTCMonth() !== mese - 1 || d.getUTCDate() !== giorno) {
    return null;
  }
  return d.toISOString().slice(0, 10);
}

// ── 🕰️ L'ORA SCRITTA DA NOI ─────────────────────────────────────────────────
//
// 🗣️ 17/09/2026, sua: *«Sistema anche le ore in italiano»* — dopo la stessa
// cura fatta alle date il giorno prima.
//
// 🚨 È lo stesso difetto, nello stesso punto: un `<input type="time">` lo
// disegna il **browser** con la SUA lingua, e dove quella lingua è l'inglese
// d'America un orario esce `07:30 PM`. Non è una nostra scelta sbagliata: non
// è una nostra scelta. ⇒ L'unico modo di scrivere le ore in italiano è
// smettere di farle scrivere a lui.
//
// ⚖️ E qui la posta è più alta che per le date, perché un orario all'americana
// non si legge male: si legge **al contrario**. `07:30` senza il `PM` accanto
// è la mattina, e una fascia di listino che comincia la mattina invece che di
// sera sposta un prezzo su mezza giornata sbagliata.

/** Quello che si fa vedere: `19:30`, oppure niente se non è un'ora. */
export function oraScritta(valore: string): string {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(valore) ? valore : '';
}

/**
 * Da quello che si batte a `HH:MM`, o `null` se non è un'ora.
 *
 * ⭐ Si accetta come viene: `19:30` · `19.30` · `19,30` · `19 30` · `1930` ·
 * `930` (= 09:30). E **all'uscita dal campo** anche l'ora nuda: `19` → `19:00`.
 *
 * 🚨 L'ora nuda vale SOLO all'uscita (`tollerante`), ed è la stessa ragione per
 * cui l'anno corto vale solo lì: battendo `1930` si passa per `1` e per `19`,
 * e prenderli per le 01:00 e le 19:00 farebbe cambiare il prezzo due volte a
 * metà di ogni battuta — e in un campo «Dalle» quel salto lo vede la griglia.
 *
 * ⛔ Le 24:00 NON si accettano: qui l'ora è un istante della giornata
 * (`oraSchema` si ferma alle 23:59). La mezzanotte come CHIUSURA è un'altra
 * cosa e ha il suo campo, con la sua regola — ⇒ `Fasce`, «e chiude alle».
 */
export function daOraItaliana(testo: string, tollerante = false): string | null {
  const pezzi = testo
    .trim()
    .split(/[^0-9]+/)
    .filter(Boolean);
  let o: string;
  let m: string;
  if (pezzi.length === 2) {
    [o, m] = pezzi as [string, string];
  } else if (pezzi.length === 1) {
    const c = pezzi[0]!;
    if (c.length === 4) [o, m] = [c.slice(0, 2), c.slice(2)];
    else if (c.length === 3) [o, m] = [c.slice(0, 1), c.slice(1)];
    else if (c.length <= 2 && tollerante) [o, m] = [c, '0'];
    else return null;
  } else return null;
  if (o.length > 2 || m.length > 2) return null;
  const ore = Number(o);
  const minuti = Number(m);
  if (!Number.isInteger(ore) || !Number.isInteger(minuti)) return null;
  if (ore < 0 || ore > 23 || minuti < 0 || minuti > 59) return null;
  return `${String(ore).padStart(2, '0')}:${String(minuti).padStart(2, '0')}`;
}
